/**
 * KrpanoCanvasCapture — Captures the krpano 360° canvas as a JPEG image
 * with camera metadata for the Visual Context-Aware Agent pipeline.
 *
 * Responsibilities:
 * - Capture the krpano WebGL canvas as a resized JPEG base64 string
 * - Read camera metadata (hlookat, vlookat, fov, scene) from krpano API
 * - Throttle captures to a configurable interval (default 1000ms per Gemini
 *   Live API limit of 1 frame/second for video input)
 * - Provide a non-blocking, memory-safe capture pipeline
 *
 * Usage:
 *   const capturer = new KrpanoCanvasCapture({ maxSize: 512, throttleMs: 1000 });
 *   const result = capturer.captureIfReady();
 *   if (result) {
 *     // result.imageBase64, result.camera.hlookat, etc.
 *   }
 */

import type { KrpanoType } from '@/types/krpano';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Camera orientation + scene info read from the krpano viewer. */
export interface CameraMetadata {
  /** Horizontal look-at angle in degrees (-180 → 180). */
  hlookat: number;
  /** Vertical look-at angle in degrees (-90 → 90). */
  vlookat: number;
  /** Current field-of-view in degrees. */
  fov: number;
  /** Active scene name (from krpano `xml.scene`). */
  scene: string;
}

/** Result of a single canvas capture. */
export interface CaptureResult {
  /** JPEG image encoded as base64 (no `data:image/…` prefix). */
  imageBase64: string;
  /** Approximate byte-size of the raw JPEG (base64 length × 0.75). */
  imageSizeBytes: number;
  /** Timestamp when this capture was taken (epoch ms). */
  timestamp: number;
  /** Camera orientation at the moment of capture. */
  camera: CameraMetadata;
  /** Time spent on the capture operation in milliseconds. */
  captureTimeMs: number;
  /** Spatial context — hotspots within viewport at capture time (populated by caller). */
  spatialContext?: import('./krpano-spatial-context').KrpanoSpatialContext;
}

/** Configuration options for KrpanoCanvasCapture. */
export interface CaptureOptions {
  /** Max width/height of the output image (default 512px). */
  maxSize?: number;
  /** JPEG quality 0–1 (default 0.75 = 75%). */
  jpegQuality?: number;
  /** Minimum interval between captures in ms (default 1000 — Gemini Live
   *  API accepts max 1 video frame/second). */
  throttleMs?: number;
  /** DOM id of the krpano embed element (default "panorama"). */
  krpanoElementId?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_MAX_SIZE = 512;
const DEFAULT_JPEG_QUALITY = 0.75;
const DEFAULT_THROTTLE_MS = 1000;
const DEFAULT_KRPANO_ELEMENT_ID = 'panorama';
/** The rAF loop keeps capturing only this long after the last demand
 *  (construction or captureIfReady call), then suspends so an idle agent costs
 *  the 360 viewer nothing. */
const DEMAND_WINDOW_MS = 4000;
/** A buffered frame older than this is dropped on re-arm so a stale view is never sent. */
const MAX_FRAME_AGE_MS = 2500;

// ---------------------------------------------------------------------------
// Helpers (pure functions — easy to unit-test in isolation)
// ---------------------------------------------------------------------------

/**
 * Safely read a numeric property from a krpano instance.
 * Returns `fallback` when the value is absent, empty, or NaN.
 */
export function safeGetNumber(
  krpano: KrpanoType,
  property: string,
  fallback: number = 0,
): number {
  try {
    const raw = krpano.get(property);
    if (raw === null || raw === undefined || raw === '') return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Safely read a string property from a krpano instance.
 */
export function safeGetString(
  krpano: KrpanoType,
  property: string,
  fallback: string = '',
): string {
  try {
    const raw = krpano.get(property);
    return raw != null ? String(raw) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Read the current camera metadata from a krpano instance.
 */
export function readCameraMetadata(krpano: KrpanoType): CameraMetadata {
  return {
    hlookat: safeGetNumber(krpano, 'view.hlookat'),
    vlookat: safeGetNumber(krpano, 'view.vlookat'),
    fov: safeGetNumber(krpano, 'view.fov', 90),
    scene: safeGetString(krpano, 'xml.scene'),
  };
}

/**
 * Find the krpano WebGL canvas element in the DOM.
 *
 * The krpano embed creates a `<canvas>` inside its container element.
 * We first check for `#panorama canvas` (the most common layout), then
 * fall back to any visible canvas with a WebGL context.
 */
export function findKrpanoCanvas(
  elementId: string = DEFAULT_KRPANO_ELEMENT_ID,
): HTMLCanvasElement | null {
  // Primary: canvas inside the krpano container div
  const container = document.getElementById(elementId);
  if (container) {
    // The krpano element itself might be the canvas in HTML5 mode
    if (container instanceof HTMLCanvasElement) return container;
    const canvas = container.querySelector('canvas') as HTMLCanvasElement | null;
    if (canvas) return canvas;
  }

  // Fallback: look for any canvas with a webgl context that looks like krpano
  const allCanvases = document.querySelectorAll('canvas');
  for (const c of allCanvases) {
    if (c.width > 100 && c.height > 100) {
      const gl = c.getContext('webgl2') || c.getContext('webgl');
      if (gl) return c;
    }
  }

  return null;
}

/**
 * Resize a source canvas into a smaller offscreen canvas and return JPEG base64.
 *
 * Reuses the provided `offscreenCanvas` to avoid GC pressure from creating
 * a new canvas every capture cycle.
 */
export function captureAndResize(
  source: HTMLCanvasElement,
  offscreen: HTMLCanvasElement,
  maxSize: number,
  jpegQuality: number,
): string {
  const scale = Math.min(maxSize / source.width, maxSize / source.height, 1);
  const w = Math.round(source.width * scale);
  const h = Math.round(source.height * scale);

  if (offscreen.width !== w) offscreen.width = w;
  if (offscreen.height !== h) offscreen.height = h;

  const ctx = offscreen.getContext('2d');
  if (!ctx) {
    throw new Error('KrpanoCanvasCapture: failed to get 2d context on offscreen canvas');
  }

  ctx.drawImage(source, 0, 0, w, h);
  const dataUrl = offscreen.toDataURL('image/jpeg', jpegQuality);
  // Strip the "data:image/jpeg;base64," prefix
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.substring(commaIndex + 1) : dataUrl;
}

// ---------------------------------------------------------------------------
// Main class
// ---------------------------------------------------------------------------


export class KrpanoCanvasCapture {
  private readonly maxSize: number;
  private readonly jpegQuality: number;
  private readonly throttleMs: number;
  private readonly krpanoElementId: string;

  /** Reusable offscreen canvas — avoids GC pressure from repeated allocation. */
  private offscreenCanvas: HTMLCanvasElement | null = null;

  /** Timestamp of the last successful capture (for throttle gating). */
  private lastCaptureTime: number = 0;

  /**
   * Pre-captured buffer — populated by the background rAF loop during the
   * render cycle (when WebGL pixels are still available). This solves the
   * `preserveDrawingBuffer=false` black-image problem.
   */
  private latestCapture: CaptureResult | null = null;

  /** ID of the running requestAnimationFrame loop (null when stopped). */
  private rafId: number | null = null;

  /** Whether the background capture loop is active. */
  private isRunning: boolean = false;

  /** Last time a consumer asked for a frame; drives loop suspend/resume. */
  private lastDemandTime: number = 0;

  /** Set after dispose() so a late captureIfReady cannot restart the loop. */
  private disposed: boolean = false;

  constructor(options: CaptureOptions = {}) {
    this.maxSize = options.maxSize ?? DEFAULT_MAX_SIZE;
    this.jpegQuality = options.jpegQuality ?? DEFAULT_JPEG_QUALITY;
    this.throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
    this.krpanoElementId = options.krpanoElementId ?? DEFAULT_KRPANO_ELEMENT_ID;

    // Automatically start the background capture loop
    this.startBackgroundCapture();
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Attempt a capture. Returns `null` if:
   * - throttle interval has not elapsed since the last capture
   * - no pre-captured frame is available yet
   *
   * This reads from the pre-captured buffer (populated by the background
   * rAF loop), so it is always safe to call from any context (audio
   * callback, timer, etc.) — no WebGL timing issues.
   */
  captureIfReady(): CaptureResult | null {
    const now = Date.now();
    this.lastDemandTime = now;
    if (this.disposed) return null;
    if (!this.isRunning) {
      // Loop was suspended while idle: the buffered frame is stale, refresh it.
      if (this.latestCapture && now - this.latestCapture.timestamp > MAX_FRAME_AGE_MS) {
        this.latestCapture = null;
      }
      this.startBackgroundCapture();
    }
    if (now - this.lastCaptureTime < this.throttleMs) {
      return null; // Too soon — respect Gemini Live 1 fps limit
    }

    if (!this.latestCapture) {
      return null;
    }

    this.lastCaptureTime = now;
    return this.latestCapture;
  }

  /**
   * Like `captureIfReady()` but, when the loop was suspended and no fresh frame
   * is buffered yet, waits (up to `timeoutMs`) for the resumed loop to produce
   * one. Intended for one-shot consumers such as visual questions.
   */
  async captureWhenReady(timeoutMs: number = 800): Promise<CaptureResult | null> {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      const result = this.captureIfReady();
      if (result || this.disposed || Date.now() >= deadline) return result;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  /**
   * Force a capture regardless of throttle. Throws on failure.
   *
   * Use `captureIfReady()` for production code; use `capture()` in tests
   * or when you need an unconditional snapshot.
   */
  capture(): CaptureResult {
    const start = performance.now();

    // 1. Locate krpano instance & canvas
    const krpano = this.getKrpano();
    if (!krpano) {
      throw new Error('KrpanoCanvasCapture: window.krpano not available');
    }

    const canvas = findKrpanoCanvas(this.krpanoElementId);
    if (!canvas) {
      throw new Error('KrpanoCanvasCapture: krpano canvas not found in DOM');
    }

    // 2. Read camera metadata
    const camera = readCameraMetadata(krpano);

    // 3. Resize & encode to JPEG base64
    if (!this.offscreenCanvas) {
      this.offscreenCanvas = document.createElement('canvas');
    }
    const imageBase64 = captureAndResize(
      canvas,
      this.offscreenCanvas,
      this.maxSize,
      this.jpegQuality,
    );

    const captureTimeMs = performance.now() - start;
    const timestamp = Date.now();

    return {
      imageBase64,
      imageSizeBytes: Math.round(imageBase64.length * 0.75),
      timestamp,
      camera,
      captureTimeMs,
    };
  }

  /**
   * Release the offscreen canvas reference and stop the background loop.
   * Call this when the agent session ends to free memory.
   */
  dispose(): void {
    this.disposed = true;
    this.stopBackgroundCapture();
    this.offscreenCanvas = null;
    this.latestCapture = null;
    this.lastCaptureTime = 0;
  }

  /**
   * Reset the throttle timer. Next call to `captureIfReady()` will capture
   * immediately. Useful after scene changes.
   */
  resetThrottle(): void {
    this.lastCaptureTime = 0;
  }

  // -------------------------------------------------------------------------
  // Background Capture Loop
  // -------------------------------------------------------------------------

  /**
   * Start a continuous requestAnimationFrame loop that captures the krpano
   * canvas every ~1 second DURING the browser render cycle.
   *
   * This is critical because WebGL canvases with `preserveDrawingBuffer=false`
   * (krpano default) clear their pixel buffer after compositing. By capturing
   * inside rAF (which runs BEFORE compositing), we read valid pixel data.
   */
  private startBackgroundCapture(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastDemandTime = Date.now();

    let lastBgCapture = 0;

    const loop = () => {
      if (!this.isRunning) return;

      const now = Date.now();
      // No consumer asked for a frame recently: suspend instead of polling the
      // WebGL canvas (drawImage + toDataURL) while competing with krpano.
      if (now - this.lastDemandTime > DEMAND_WINDOW_MS) {
        this.isRunning = false;
        this.rafId = null;
        return;
      }
      // Only capture at roughly the throttle interval to save CPU
      if (now - lastBgCapture >= this.throttleMs) {
        try {
          const result = this.capture();
          // Validate the capture is not just a black image (> 3KB minimum)
          if (result.imageSizeBytes > 3000) {
            // Compute spatial context at the same moment as the frame capture
            // to ensure hotspot visibility matches the captured image exactly
            try {
              const { computeSpatialContext } = require('./krpano-spatial-context');
              result.spatialContext = computeSpatialContext(this.getKrpano()) ?? undefined;
            } catch {
              // Spatial context is optional — capture still valid without it
            }
            this.latestCapture = result;
          }
          lastBgCapture = now;
        } catch {
          // Silently skip — krpano might not be ready yet
        }
      }

      this.rafId = requestAnimationFrame(loop);
    };

    this.rafId = requestAnimationFrame(loop);
    console.log('[KrpanoCanvasCapture] Background capture loop started (rAF-based, ~1fps, demand-driven)');
  }

  /** Stop the background capture loop. */
  private stopBackgroundCapture(): void {
    this.isRunning = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  /** Get the krpano instance from the global scope. */
  private getKrpano(): KrpanoType | null {
    if (typeof window === 'undefined') return null;
    const k = (window as any).krpano;
    if (k && typeof k.get === 'function') return k as KrpanoType;
    return null;
  }
}

